import { useEffect, useMemo, useState } from "react"
import * as gqlTypes from "@hydrologiq/gql/graphql"
import { VStack, Text, HStack, Link, Box, Collapsible, Accordion, Button, Separator } from "@chakra-ui/react"
import { ExpandButton, CollapseButton, ArchiveButton } from "@hydrologiq/shared/content/components/Button"
import StatusBadge from "@hydrologiq/shared/components/StatusBadge"
import { toUserTZ } from "@hydrologiq/common/helpers/date"
import { DateTime, utc } from "timezonecomplete"
import { formatStatus } from "@hydrologiq/shared/utils/activities/status"
import ActivityLinkSelector, { ACTIVITY_LINK_ENABLED } from "../ActivityLinkSelector"
import OrderBanner from "../OrderBanner"
import { ActivityEditProps, ActivityManageScreen } from "./screens"
import { useCurrentActivity, useCurrentActivityStatus } from "@hydrologiq/shared/contexts/ActivityWorkflowContextV2"
import { getNextAction } from "@hydrologiq/shared/utils/activities/activityStatus"
import { useActivityWorkflowActions, useLinkedSiteDefaults } from "@hydrologiq/shared/contexts/ActivityWorkflowContextV2"
import ActivityLoading from "./ActivityLoading"
import FormContainer from "@hydrologiq/shared/chakra/custom/FormContainer"

interface ActivityEditHomeProps extends ActivityEditProps {
  readonly?: boolean
  allowDelete?: boolean
}

const formatDate = (isoDate: string | undefined): string => {
  if (!isoDate) return ""
  try {
    return new DateTime(isoDate).format("yyyy-MM-dd HH:mm")
  } catch {
    return isoDate
  }
}

const ActivityEditHome = ({ readonly, allowDelete, onScreenChange, onChange }: ActivityEditHomeProps) => {
  const activity = useCurrentActivity()
  if (!activity) {
    return <ActivityLoading />
  }
  const currentStatus = useCurrentActivityStatus()
  const { setCurrentEventKind, needToRefresh, setLinkedSiteDefaults } = useActivityWorkflowActions()
  const linkedSiteDefaults = useLinkedSiteDefaults()
  const { nextEventKind, buttonText } = useMemo(() => getNextAction(currentStatus), [currentStatus])

  const [attributesSectionExpanded, setAttributesSectionExpanded] = useState(true)
  const [activityLinksSectionExpanded, setActivityLinksSectionExpanded] = useState(false)
  const [expandedAccordionItem, setExpandedAccordionItem] = useState<string[]>([])

  // Group attributes by event kind for display
  const attributesByEventKind = useMemo(() => {
    const grouped: Record<gqlTypes.ActivityEventKind, any[]> = {
      [gqlTypes.ActivityEventKind.Scheduled]: [],
      [gqlTypes.ActivityEventKind.Pending]: [],
      [gqlTypes.ActivityEventKind.Started]: [],
      [gqlTypes.ActivityEventKind.Completed]: [],
    }

    activity.attributes?.forEach((attr) => {
      grouped[attr.event_kind].push(attr)
    })

    return grouped
  }, [activity.attributes])

  // Check which statuses have events (have been reached)
  const hasEvent = useMemo(() => {
    const eventKinds = new Set(activity.events?.map((e) => e.kind) || [])
    return {
      Pending:
        eventKinds.has(gqlTypes.ActivityEventKind.Scheduled) || eventKinds.has(gqlTypes.ActivityEventKind.Pending),
      Started: eventKinds.has(gqlTypes.ActivityEventKind.Started),
      Completed: eventKinds.has(gqlTypes.ActivityEventKind.Completed),
    }
  }, [activity.events])

  // Map display status to event kind
  const statusToEventKind = {
    Scheduled: gqlTypes.ActivityEventKind.Scheduled,
    "In Progress": gqlTypes.ActivityEventKind.Started,
    Completed: gqlTypes.ActivityEventKind.Completed,
  }

  // All possible statuses in order
  const allStatuses: Array<"Scheduled" | "In Progress" | "Completed"> = ["Scheduled", "In Progress", "Completed"]

  // Get color for status based on state
  const getStatusColor = (isCurrent: boolean, hasReached: boolean): string => {
    if (isCurrent) {
      return "active" // Primary color for current status
    }
    if (hasReached) {
      return "fg.muted" // Muted for completed statuses
    }
    return "fg" // Default for future statuses
  }

  // Map current status to display names
  const currentStatusDisplay =
    currentStatus === "Pending" ? "Scheduled" : currentStatus === "Started" ? "In Progress" : "Completed"

  useEffect(() => {
    setExpandedAccordionItem([currentStatusDisplay])
  }, [currentStatusDisplay])

  const canDelete = allowDelete ?? !readonly

  const actions =
    canDelete || nextEventKind
      ? () => (
          <HStack justify="space-between" width="100%">
            {canDelete && (
              <ArchiveButton
                onClick={() => onScreenChange(ActivityManageScreen.ConfirmDelete)}
                context={{ description: "Delete the current activity", label: "Delete Activity", variant: "warning" }}
              />
            )}
            {nextEventKind && (
              <Box>
                <Button
                  variant="hydrologiq_primary"
                  onClick={() => onScreenChange(ActivityManageScreen.ProgressStatus)}
                >
                  {buttonText} Activity
                </Button>
              </Box>
            )}
          </HStack>
        )
      : undefined

  return (
    <FormContainer ActionOverride={actions}>
      <VStack gap={2} align="stretch" width="100%">
        {/* Order Context Banner - at top like creation workflow */}
        {activity.order && <OrderBanner order={activity.order} titlePrefix="Order:" showIcon={false} />}

        {/* Activity Window Section */}
        <VStack gap={2} align="stretch" width="100%">
          <HStack justify="space-between">
            <Text fontWeight="semibold">Information</Text>
            {!readonly && (
              <Link variant="primary" cursor="pointer" onClick={() => onScreenChange(ActivityManageScreen.EditDetails)}>
                Edit details
              </Link>
            )}
          </HStack>
          <HStack gap={2} justify="space-between" height="100%" alignItems="start">
            <VStack gap={2} align="flex-start">
              <Text>
                Start: {activity.start ? formatDate(toUserTZ(activity.start, utc()).toIsoString()) : "Not set"}
              </Text>
              <Text>End: {activity.end ? formatDate(toUserTZ(activity.end, utc()).toIsoString()) : "Not set"}</Text>
              <Text>
                Asset: {activity.assets && activity.assets.length > 0 ? activity.assets[0].name : "No asset assigned"}
              </Text>
            </VStack>
          </HStack>
        </VStack>
        <Separator />
        <VStack
          gap={2}
          align="stretch"
          width="100%"
          display={activity.kind === gqlTypes.ActivityKind.Movement && ACTIVITY_LINK_ENABLED ? undefined : "none"}
        >
          <HStack gap={2} justify="space-between">
            <Text fontWeight="semibold">Activity Connections</Text>
            {activityLinksSectionExpanded ? (
              <CollapseButton
                size="xs"
                onClick={() => setActivityLinksSectionExpanded(false)}
                aria-label="Collapse attributes section"
              />
            ) : (
              <ExpandButton
                size="xs"
                onClick={() => setActivityLinksSectionExpanded(true)}
                aria-label="Expand attributes section"
              />
            )}
          </HStack>

          <Collapsible.Root open={activityLinksSectionExpanded}>
            <Collapsible.Content>
              <ActivityLinkSelector
                mode="edit"
                activity={activity}
                onLinkCreated={(_targetActivityId, linkType, linkedActivity, removed) => {
                  // linkType "destination" (activity before) → Source attribute
                  // linkType "source" (activity after) → Destination attribute
                  const field = linkType === "destination" ? "source" : "destination"
                  const eventKind = statusToEventKind[currentStatusDisplay]

                  // Get existing attribute value for comparison
                  const existingAttr = activity.attributes?.find(
                    (attr) => attr.attribute === field && attr.event_kind === eventKind
                  )
                  const existingSiteId = existingAttr?.site?.id || null

                  const newSiteId = !removed
                    ? (linkedActivity?.site || linkedActivity?.destination_site)?.id || null
                    : null

                  // Only flag a change if the site actually differs from what's saved
                  if (newSiteId !== existingSiteId) {
                    setLinkedSiteDefaults({ [field]: newSiteId })
                  }

                  needToRefresh("silent")
                  onChange()
                }}
              />
            </Collapsible.Content>
          </Collapsible.Root>
        </VStack>
        <Separator
          display={activity.kind === gqlTypes.ActivityKind.Movement && ACTIVITY_LINK_ENABLED ? undefined : "none"}
        />

        {/* Status Section */}
        <VStack gap={2} align="stretch" width="100%">
          <HStack justify="space-between">
            <Text fontWeight="semibold">Status</Text>
            <HStack>
              <StatusBadge
                status={
                  currentStatus === "Pending" ? "scheduled" : currentStatus === "Started" ? "in_progress" : "completed"
                }
              />
              {attributesSectionExpanded ? (
                <CollapseButton
                  size="xs"
                  onClick={() => setAttributesSectionExpanded(false)}
                  aria-label="Collapse attributes section"
                />
              ) : (
                <ExpandButton
                  size="xs"
                  onClick={() => setAttributesSectionExpanded(true)}
                  aria-label="Expand attributes section"
                />
              )}
            </HStack>
          </HStack>

          <Collapsible.Root open={attributesSectionExpanded}>
            <Collapsible.Content px={1} fontSize="sm">
              <Accordion.Root
                collapsible
                value={expandedAccordionItem}
                onValueChange={(details) => setExpandedAccordionItem(details.value)}
              >
                {allStatuses.map((statusName) => {
                  const eventKind = statusToEventKind[statusName]
                  const isCurrent = statusName === currentStatusDisplay
                  const hasReached =
                    statusName === "Scheduled"
                      ? hasEvent.Pending
                      : statusName === "In Progress"
                      ? hasEvent.Started
                      : hasEvent.Completed
                  const statusColor = getStatusColor(isCurrent, hasReached)
                  const attrs = attributesByEventKind[eventKind] || []
                  const isExpanded = expandedAccordionItem.includes(statusName)

                  return (
                    <Accordion.Item key={statusName} value={statusName}>
                      <Accordion.ItemTrigger fontSize="sm">
                        <HStack gap={2} justify="space-between" width="100%">
                          <HStack gap={2} flex="1">
                            <Text fontWeight={isCurrent ? "bold" : "normal"}>{statusName}</Text>
                            {isCurrent && (
                              <Text fontWeight="normal" color={statusColor}>
                                [active]
                              </Text>
                            )}
                          </HStack>
                          <HStack gap={2}>
                            <Link
                              variant="primary"
                              cursor="pointer"
                              onClick={(e) => {
                                e.stopPropagation()
                                setCurrentEventKind(eventKind)
                                onScreenChange(ActivityManageScreen.EditAttribute)
                              }}
                            >
                              {isCurrent && (linkedSiteDefaults?.source !== undefined || linkedSiteDefaults?.destination !== undefined)
                                ? "Save changes"
                                : "Edit"}
                            </Link>
                            {/* <Accordion.ItemTrigger asChild>
                            {isExpanded ? (
                              <CollapseButton size="xs" aria-label={`Collapse ${statusName} attributes`} />
                            ) : (
                              <ExpandButton size="xs" aria-label={`Expand ${statusName} attributes`} />
                            )}
                          </Accordion.ItemTrigger> */}
                          </HStack>
                        </HStack>
                      </Accordion.ItemTrigger>
                      <Accordion.ItemContent>
                        {attrs.length === 0 ? (
                          <Text color="fg.muted" ml={4}>
                            No attributes set
                          </Text>
                        ) : (
                          <VStack gap={1} align="stretch" ml={4}>
                            {attrs.map((attr, index) => {
                              let label = ""
                              let value = ""

                              if (
                                [
                                  gqlTypes.ActivityAttributeKind.Site,
                                  gqlTypes.ActivityAttributeKind.Source,
                                  gqlTypes.ActivityAttributeKind.Destination,
                                ].includes(attr.attribute)
                              ) {
                                label = formatStatus(attr.attribute)
                                value = attr.site?.name || attr.value || ""
                              } else if (attr.attribute === gqlTypes.ActivityAttributeKind.Batch) {
                                label = formatStatus(attr.attribute)
                                value = attr.batch?.name || attr.batch?.id || attr.value || ""
                              } else if (attr.attribute === gqlTypes.ActivityAttributeKind.Pressure) {
                                label = formatStatus(attr.attribute)
                                value = attr.value ? `${attr.value} bar` : ""
                              } else if (attr.attribute === gqlTypes.ActivityAttributeKind.Temperature) {
                                label = formatStatus(attr.attribute)
                                value = attr.value ? `${attr.value}°C` : ""
                              } else if (attr.attribute === gqlTypes.ActivityAttributeKind.Quantity) {
                                label = formatStatus(attr.attribute)
                                value = attr.value ? `${attr.value} kg` : ""
                              } else if (attr.attribute === gqlTypes.ActivityAttributeKind.Note) {
                                label = formatStatus(attr.attribute)
                                value = attr.value || ""
                              } else {
                                label = formatStatus(attr.attribute)
                                value = attr.value || ""
                              }

                              return (
                                <Text key={index} color="fg.muted">
                                  {label}: {value}
                                </Text>
                              )
                            })}
                          </VStack>
                        )}
                      </Accordion.ItemContent>
                    </Accordion.Item>
                  )
                })}
              </Accordion.Root>
            </Collapsible.Content>
          </Collapsible.Root>

          {/* Delete Activity Button */}
        </VStack>
      </VStack>
    </FormContainer>
  )
}

export default ActivityEditHome
